import {useState} from "react";
import {Alert, Box, Button, CircularProgress, Typography} from "@mui/material";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import {Navigate, useNavigate} from "react-router-dom";
import {flushSync} from "react-dom";
import KioskHeader from "../components/KioskHeader";
import KioskSteps from "../components/KioskSteps";
import {useKioskLang, useKioskSession} from "../context/KioskSessionContext";
import {createKioskOrder} from "../api/kioskOrders";
import {cartComboDiscount} from "../orderUtils";

export default function PaymentMethodPage({systemData, online, onHome}) {
  const navigate = useNavigate();
  const {state, dispatch, saveRecovery} = useKioskSession();
  const {tr} = useKioskLang();
  const [loading, setLoading] = useState("");
  const [error, setError] = useState("");
  if (!state.sessionId || !state.cart.length || !state.pricing) return <Navigate to="/kiosk/cart" replace/>;
  const settings = systemData?.kiosk_settings ?? {};
  //套餐優惠已含在購物車品項價格，這裡只顯示訂單層級的折抵（折扣碼、跨品項自動折扣）
  const comboDiscount = cartComboDiscount(systemData, state.cart);
  const orderDiscount = Math.max(0, Math.abs(Number(state.pricing.discount ?? 0)) - comboDiscount);
  const mealsAmount = Number(state.pricing.subtotal) - comboDiscount;

  const choose = async (flow) => {
    if (!online) return;
    setLoading(flow);
    setError("");
    try {
      const response = await createKioskOrder({
        session_id: state.sessionId,
        method: state.orderMethod,
        payment_flow: flow,
        buycart: state.cart,
        quote_token: state.pricing.quote_token,
        member_mobile: state.memberMobile,
        table_number: state.tableNumber,
        coupon_code: state.couponCode,
      });
      if (response.error !== 0) {
        if (response.requires_requote) {
          flushSync(() => dispatch({type: "SET_PRICING", pricing: null}));
          navigate("/kiosk/cart");
          return;
        }
        if (response.coupon_invalid) dispatch({type: "CLEAR_COUPON"});
        setError(response.msg || tr("createOrderFailed"));
        return;
      }
      const stage = flow === "line_pay" ? "awaiting_line_pay" : "awaiting_counter_payment";
      flushSync(() => dispatch({type: "SET_SERVER_ORDER", order: response.order, paymentMethod: flow, stage}));
      saveRecovery({
        sessionId: state.sessionId,
        orderId: response.order.order_id,
        orderNo: response.order.order_no,
        orderMethod: state.orderMethod,
        tableNumber: state.tableNumber,
        memberMobile: state.memberMobile,
        pricing: response.order.pricing,
        paymentMethod: flow,
      });
      //櫃檯付款不再有獨立頁：建單後直接進完成頁（號碼＋金額＋櫃檯指引）
      navigate(flow === "line_pay" ? "/kiosk/line-pay" : "/kiosk/complete", flow === "line_pay" ? undefined : {replace: true});
    } catch {
      setError(tr("createOrderNetworkError"));
    } finally {
      setLoading("");
    }
  };

  const methods = [
    settings.allow_line_pay !== false && {id: "line_pay", title: "Line Pay", text: tr("linePayText"), icon: <QrCodeScannerRoundedIcon sx={{fontSize: 72}}/>},
    settings.allow_counter_pay !== false && {id: "counter", title: tr("counterCheckout"), text: tr("counterCheckoutText"), icon: <PaymentsRoundedIcon sx={{fontSize: 72}}/>},
  ].filter(Boolean);

  return <Box sx={{height: "100dvh", display: "flex", flexDirection: "column"}}>
    <KioskHeader systemData={systemData} onHome={onHome} method={state.orderMethod} tableNumber={state.tableNumber} onBack={() => navigate("/kiosk/cart")} online={online}/>
    <KioskSteps active={2}/>
    <Box sx={{flex: 1, p: 4, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center"}}>
      <Typography sx={{fontSize: 38, fontWeight: 950}}>{tr("choosePayment")}</Typography>
      {orderDiscount > 0 ? (
        <Box sx={{mt: 1.5, px: 3, py: 1.5, bgcolor: "#FFF", borderRadius: 1.5, border: "1px solid rgba(80,55,40,.12)", textAlign: "center"}}>
          <Typography sx={{fontSize: 17, color: "text.secondary"}}>
            {tr("discountLine", {subtotal: mealsAmount.toLocaleString(), discount: orderDiscount.toLocaleString()})}
            {state.pricing.coupon?.name ? `（${state.pricing.coupon.name}）` : ""}
          </Typography>
          <Typography sx={{fontSize: 30, fontWeight: 950, color: "primary.main"}}>{tr("amountDue", {n: Number(state.pricing.total).toLocaleString()})}</Typography>
        </Box>
      ) : (
        <Typography sx={{fontSize: 30, fontWeight: 950, color: "primary.main", mt: 1}}>{tr("amountDue", {n: Number(state.pricing.total).toLocaleString()})}</Typography>
      )}
      {state.memberMobile && <Typography sx={{fontSize: 16, color: "text.secondary", mt: 1}}>{tr("pointsMobile", {mobile: state.memberMobile})}</Typography>}
      {error && <Alert severity="error" sx={{mt: 2, width: "min(100%, 780px)"}}>{error}</Alert>}
      <Box sx={{width: "min(100%, 880px)", mt: 3.5, display: "grid", gridTemplateColumns: `repeat(${methods.length},1fr)`, gap: 2.5}}>
        {methods.map((method) => <Button key={method.id} onClick={() => choose(method.id)} disabled={Boolean(loading) || !online} sx={{height: 280, bgcolor: "#FFF", color: "text.primary", display: "flex", flexDirection: "column", gap: 2, borderRadius: 1.5, border: "2px solid rgba(80,55,40,.12)", boxShadow: "0 12px 32px rgba(76,49,32,.08)", "&:hover": {bgcolor: "#FFF8F0", borderColor: "primary.main"}}}>
          {loading === method.id ? <CircularProgress size={60}/> : <Box sx={{color: "primary.main"}}>{method.icon}</Box>}
          <Typography sx={{fontSize: 29, fontWeight: 950}}>{method.title}</Typography>
          <Typography color="text.secondary" sx={{fontSize: 16.5}}>{method.text}</Typography>
        </Button>)}
      </Box>
    </Box>
  </Box>;
}
